import { useEffect, useMemo, useState } from 'react'
import { Diagram } from '../Diagram'
import { useSelector } from 'react-redux'
import { selectedRangeSelector } from 'redux/dashboard/selectors'
import { dashboardApi } from 'api'
import { useApiRequest } from 'hooks/useApiRequest'
import { RevenuePerCarClassResponse } from 'api/dashboard/types'
import { diagramVariant, PERIOD } from '../../constants'
import { REVENUE_PER_CAR_CLASS_DIAGRAM } from 'texts/dashboard'
import { supplierCurrencySelector } from 'redux/login/selectors'
import { RevenuePerCarClassProps } from './types'
import { CURRENCY_SYMBOL } from 'constants/currencies'
import { useCompanyId } from 'hooks/useCompanyId'

export const RevenuePerCarClass = ({
  isShortPeriod,
  carCategories,
}: RevenuePerCarClassProps) => {
  const [items, setItems] = useState<RevenuePerCarClassResponse['items']>([])
  const [period, setSelectedPeriod] = useState(PERIOD.Days)
  const selectedRange = useSelector(selectedRangeSelector)
  const supplierCurrency = useSelector(supplierCurrencySelector)

  const companyId = useCompanyId()

  const getRevenuePerCarClassRequest = useApiRequest(() =>
    dashboardApi.getRevenuePerCarClass({
      companyId: String(companyId),
      dateStart: selectedRange.from,
      dateEnd: selectedRange.to,
      carCategories,
      timeGrain: period,
    })
  )

  useEffect(() => {
    if (isShortPeriod) {
      setSelectedPeriod(PERIOD.Weeks)
    }
  }, [isShortPeriod])

  useEffect(() => {
    const getSchedule = async () => {
      const response = await getRevenuePerCarClassRequest.apiRequest()
      if (response) {
        setItems(response.data.items)
      }
    }
    getSchedule()
  }, [selectedRange.from, selectedRange.to, period, carCategories])

  const uniqueDates = [...new Set(items.map((item) => item.date))]

  const groupedData = useMemo(
    () =>
      carCategories?.map((type) => {
        const dataObj: Record<string, number> = {}
        uniqueDates.forEach((date) => {
          dataObj[date] = 0
        })

        items.forEach((item) => {
          if (item.carCategory === type) {
            dataObj[item.date] = item.revenue
          }
        })

        const dataArray = uniqueDates.map((date) => dataObj[date] || null)

        return {
          name: type,
          data: dataArray,
        }
      }),
    [items]
  )

  return (
    <Diagram
      series={groupedData}
      categories={uniqueDates}
      onChangePeriod={setSelectedPeriod}
      groupCount={groupedData?.length}
      columnsCount={uniqueDates.length}
      variant={diagramVariant.perCarClass}
      period={period}
      isShortPeriod={isShortPeriod}
      currency={supplierCurrency}
      title={`${REVENUE_PER_CAR_CLASS_DIAGRAM.TITLE}${
        supplierCurrency ? `, ${CURRENCY_SYMBOL[supplierCurrency]}` : ''
      }`}
      subtitle={REVENUE_PER_CAR_CLASS_DIAGRAM.SUBTITLE}
      loading={getRevenuePerCarClassRequest.loading}
    />
  )
}
